<script setup lang="ts">
/**
 * UI-kit component
 * Category: Layouts
 * Name: SectionLayout
 * FIGMA: https://www.figma.com/design/NgI2cVJkWODrgnOBHO3Zed/Strapi-Builder--DS-?node-id=594-35256
 *
 * The shared wrapper for Strapi page-builder sections: surface color, section paddings,
 * centered header (title/description + optional #top slot for extra header content),
 * a content slot and an optional #bottom slot (CTA).
 * Most sections wrap their content in it — skip it only for full-bleed designs (big banners).
 * Local implementation of the Figma DS component missing from @brighterly/lib-lumee-ui (dsGap).
 */
import { ref } from 'vue'
// types
import type { SectionLayoutProps } from '../types/interfaces'
// hooks
import { useSectionReveal } from '@composables/tools/section-reveal.hook'

defineOptions({
  name: 'SectionLayout',
})

withDefaults(defineProps<SectionLayoutProps>(), {
  title: '',
  titleTag: 'h2',
  description: '',
  color: 'canvas',
  space: 'md',
})

const refInner = ref<HTMLElement | null>(null)

useSectionReveal(refInner)
</script>

<template>
  <section
    class="section-layout"
    :class="[`section-layout--${color}`, `section-layout--space-${space || 'md'}`]"
  >
    <div
      ref="refInner"
      class="section-layout__inner"
    >
      <div
        v-if="title || description || $slots.top"
        class="section-layout__header"
      >
        <component
          :is="titleTag || 'h2'"
          v-if="title"
          class="section-layout__title btly-text-heading-m md:btly-text-heading-l"
        >
          {{ title }}
        </component>
        <p
          v-if="description"
          class="section-layout__description btly-text-body-l lg:btly-text-body-xl"
        >
          {{ description }}
        </p>
        <slot name="top" />
      </div>

      <div class="section-layout__content">
        <slot />
      </div>

      <div
        v-if="$slots.bottom"
        class="section-layout__bottom"
      >
        <slot name="bottom" />
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss" src="../styles/_section-layout.scss" />
